import React from 'react';
import { FormSpy, AnyObject, FieldInputProps } from 'react-final-form';
import { FormApi, FieldValidator } from 'final-form';
import clsx from 'clsx';
import { Grid, Typography, TextField, InputAdornment, AccordionSummary, AccordionDetails } from '@material-ui/core';
import { KeyboardDatePicker } from '@material-ui/pickers/DatePicker/DatePicker';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { EnquiryDefaultConfig } from '../../EnquirySummaryRepository';
import { IAutoCompleteItem } from '../../../typings/autoComplete';
import { OtherPartyPostalAddressPanel } from './OtherPartyPostalAddressPanel';
import { OtherPartyAddressPanel } from './OtherPartyAddressPanel';
import { FieldSwitchButton } from '../components/FieldSwitchButton';
import { IndividualNameSummary, IndividualSelector } from '../../selectors/IndividualSelector';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import { Address, PlaceType } from '../../selectors/AddressSelector';
import { addEllipsis, arrayToString, arrayToStringWithSeparator } from '../../../helpers/commonHelper';
import { TranslationTextList } from '../../../locales/common';
import { TitleAutocompleteSelector } from '../../selectors/TitleAutocompleteSelector';
import CompareArrowsIcon from '@material-ui/icons/CompareArrows';
import type { Enquiry, EnquiryFormClasses } from '../EnquiryFormDrawer';
import { arePropsEqualIgnoringForm } from './arePropsEqualIgnoringForm';
// DEBUG: Field / Accordion are the original components unless the render-count flag is on (see helpers/renderCountDebug)
import { Field, createRenderCountAccordion } from '../../../helpers/renderCountDebug';

const Accordion = createRenderCountAccordion('OtherSidePanel');

// Explicit request (EnquiryFormDrawer split): the "Other Side" accordion (name, contact details, addresses).
// Moved out of EnquiryFormDrawer.tsx with its JSX and every field's name / validate / subscription /
// handler logic unchanged. The outer component subscribes to the form (FormSpy) and passes the memoized
// body only the values it displays; event handlers read the form's values at the moment the event fires
// - exactly what the render-time `values` closure held before.
export interface OtherSidePanelProps {
    form: FormApi<Enquiry>;
    classes: EnquiryFormClasses;
    isExpanded: boolean;
    isOtherSidePostalAddressPanelExpanded: boolean;
    homeCountry: string;
    enquiryDefaultConfig: EnquiryDefaultConfig | undefined;
    requiredValidator: FieldValidator<any>;
    onDropDownSelect: (form: FormApi<Enquiry>, values: AnyObject, input: FieldInputProps<any, HTMLElement>, selection: any, name: string) => void;
    onGetPartyAddresses: (form: FormApi<Enquiry>, values: AnyObject, selection: IndividualNameSummary, name: string) => void;
    onSwitch: (form: FormApi<Enquiry>, values: AnyObject, name: string) => void;
    birthDateValidator: FieldValidator<any>;
    translate: (key: keyof TranslationTextList) => string;
    getAddressLabelForPanel: (buildingName: string, level: string, streetNumber: string, street1: string, street2: string, suburb: string, addressState: string, postCode: string, country: string) => string;
    createAddressSelectionHandler: (form: FormApi<Enquiry>, values: AnyObject) => (placeType: PlaceType | null, address: Address, name: string) => void;
    createInputChangedHandler: (form: FormApi<any>, name: string | any) => (value: string) => void;
    isOtherPartyPoBoxRequired: (values: AnyObject) => boolean;
    onOtherSidePostalAddressPanelExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    onExpandChange: (event: React.ChangeEvent<{}>, expanded: boolean) => void;
    isSourceTypeClientOnly: true | undefined;
}

interface OtherSidePanelBodyProps extends OtherSidePanelProps {
    formValues: AnyObject;
}

const OtherSidePanelBody: React.FC<OtherSidePanelBodyProps> = React.memo((bodyProps) => {
    const {
        form, classes, isExpanded, isOtherSidePostalAddressPanelExpanded, homeCountry, enquiryDefaultConfig, requiredValidator, onDropDownSelect, onGetPartyAddresses, onSwitch, birthDateValidator, translate, getAddressLabelForPanel, createAddressSelectionHandler, createInputChangedHandler, isOtherPartyPoBoxRequired, onOtherSidePostalAddressPanelExpandChange, onExpandChange, isSourceTypeClientOnly,
        formValues,
    } = bodyProps;
    const values = formValues;

    const panelTitle = addEllipsis(arrayToString([
        arrayToStringWithSeparator([
            values.otherPartyTitle && values.otherPartyTitle.trim().length > 0 && values.otherPartyTitle !== 'None' ? `${values.otherPartyTitle}` : '',
            values.otherPartyFirstName,
            values.otherPartyLastName,
            // tslint:disable-next-line: align
        ], ' '),
        arrayToStringWithSeparator([
            arrayToStringWithSeparator([
                values.otherPartyFloor,
                values.otherPartyNumber,
                // tslint:disable-next-line: align
            ], '/'),
            values.otherPartyStreet,
            // tslint:disable-next-line: align
        ], ' '),
        values.otherPartyStreet2,
        values.otherPartySuburb,
        values.otherPartyState,
        values.otherPartyPostcode,
        values.otherPartyCountry
        // tslint:disable-next-line: align
    ]), 70);

    return (
        <Accordion
            className={classes.expansionPanel}
            expanded={isExpanded}
            onChange={onExpandChange}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="other-side-content"
                id="other-side-header"
                className={classes.expansionPanelSummary}
            >
                <div className={classes.sectionHeaderRow}>
                    <span className={clsx(classes.sectionIconChip, classes.sectionIconChipBlue)}>
                        <CompareArrowsIcon fontSize="small" />
                    </span>
                    <Typography className={clsx(classes.heading, classes.sectionLabelBlue, classes.sectionLabelText)}>
                        Other Side
                    </Typography>
                </div>
                <div className={classes.infoContainer}>
                    <div className="col">
                        {panelTitle}
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails
                className={classes.expandablePanelDetails}
            >
                <Grid container={true} spacing={2}>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyTitle"
                            // validate={required} 
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <>
                                    <TitleAutocompleteSelector
                                        variant="outlined"
                                        size="small"
                                        {...input}
                                        label="Title"
                                        className={classes.textField}
                                        error={meta.error && meta.touched}
                                        value={input.value ? { label: input.value, value: input.value } : undefined}
                                        // tslint:disable-next-line: max-line-length
                                        onSelection={(selection: IAutoCompleteItem, name: string) => {
                                            input.onChange(selection ? selection.value : '');
                                        }}
                                        // required={true}
                                        helperText={meta.error && meta.touched ? 'Title is required' : ''}
                                    />
                                </>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyFirstName"
                            validate={enquiryDefaultConfig && enquiryDefaultConfig.isOtherSideNameMandatory ? requiredValidator : undefined}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Given Name(s)"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    disabled={values.otherPartyNameGuid}
                                    autoComplete="abcd"
                                    required={enquiryDefaultConfig && enquiryDefaultConfig.isOtherSideNameMandatory
                                        ? enquiryDefaultConfig.isOtherSideNameMandatory
                                        : false
                                    }
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Given Name is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <div className={classes.toggleFieldWrapepr}>
                            <div className="field">
                                {
                                    values.isOtherPartyNameSwitched ? (
                                        <Field
                                            name="otherPartyIndividualSelector"
                                            validate={values.isOtherPartyNameSwitched && enquiryDefaultConfig && enquiryDefaultConfig.isOtherSideNameMandatory ? requiredValidator : undefined}
                                            subscription={{ touched: true, error: true, value: true }}
                                        >
                                            {({ input, meta }) => (
                                                <IndividualSelector
                                                    variant="outlined"
                                                    size="small"
                                                    {...input}
                                                    key="otherPartyIndividualSelector"
                                                    label="Surname"
                                                    required={enquiryDefaultConfig && enquiryDefaultConfig.isOtherSideNameMandatory
                                                            ? enquiryDefaultConfig.isOtherSideNameMandatory
                                                            : false
                                                        }
                                                    value={values.otherPartyIndividualSelector}
                                                    error={meta.error && meta.touched}
                                                    // helperText={
                                                    //     meta.error &&
                                                    //         meta.touched
                                                    //         ? 'Surname is required'
                                                    //         : ''
                                                    // }
                                                    onSelection={(selection: IndividualNameSummary, name: string) => {
                                                        const values = form.getState().values;
                                                        input.onChange(selection);
                                                        onDropDownSelect(form, values, input, selection, 'otherPartyIndividualSelector');
                                                        onGetPartyAddresses(form, values, selection, 'otherPartyIndividualSelector');
                                                    }}
                                                    enableSecondary={true}
                                                    isSourceTypeClientOnly={isSourceTypeClientOnly}
                                                    endAdornmentExtra={(
                                                        <FieldSwitchButton
                                                            tooltip="Switch to enter the last name"
                                                            onClick={() => onSwitch(form, form.getState().values, 'otherPartyLastName')}
                                                        />
                                                    )}
                                                />
                                            )}
                                        </Field>
                                    ) :
                                        (
                                            <Field
                                                name="otherPartyLastName"
                                                validate={!values.isOtherPartyNameSwitched && enquiryDefaultConfig && enquiryDefaultConfig.isOtherSideNameMandatory ? requiredValidator : undefined}
                                                subscription={{ touched: true, error: true, value: true }}
                                            >
                                                {({ input, meta }) => (
                                                    <TextField
                                                        variant="outlined"
                                                        size="small"
                                                        {...input}
                                                        label="Surname"
                                                        className={classes.textField}
                                                        error={meta.error && meta.touched}
                                                        disabled={values.otherPartyNameGuid}
                                                        autoComplete="abcd"
                                                        required={enquiryDefaultConfig && enquiryDefaultConfig.isOtherSideNameMandatory
                                                            ? enquiryDefaultConfig.isOtherSideNameMandatory
                                                            : false
                                                        }
                                                        helperText={
                                                            meta.error &&
                                                                meta.touched
                                                                ? 'Surname is required'
                                                                : ''
                                                        }
                                                        InputProps={{
                                                            endAdornment: (
                                                                <InputAdornment position="end">
                                                                    <FieldSwitchButton
                                                                        tooltip="Switch to select an existing client & contact"
                                                                        onClick={() => onSwitch(form, form.getState().values, 'otherPartyLastName')}
                                                                    />
                                                                </InputAdornment>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                            </Field>
                                        )
                                }
                            </div>
                        </div>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyEmail"
                            type="email"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <div>
                                    <TextField
                                        variant="outlined"
                                        size="small"
                                        {...input}
                                        label="Email"
                                        type="email"
                                        className={classes.textField}
                                        disabled={values.otherPartyNameGuid}
                                        // tslint:disable-next-line: no-any
                                        onChange={(event: any) => {
                                            input.onChange(event.target.value);
                                        }}
                                        error={
                                            meta.error &&
                                            meta.touched
                                        }
                                        autoComplete="abcd"
                                        helperText={
                                            meta.error &&
                                                meta.touched
                                                ? meta.error
                                                : ''
                                        }
                                    />
                                </div>
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyPhone"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Telephone"
                                    type="text"
                                    // tslint:disable-next-line: no-any
                                    onChange={(event: any) => {
                                        input.onChange(event.target.value);
                                    }}
                                    // required={true}
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? meta.error // 'Phone is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyMobile"
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    label="Mobile"
                                    type="text"
                                    // tslint:disable-next-line: no-any
                                    onChange={(event: any) => {
                                        input.onChange(event.target.value);
                                    }}
                                    // required={true}
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? meta.error // 'Mobile is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyDOB"
                            validate={birthDateValidator}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <KeyboardDatePicker
                                    inputVariant="outlined"
                                    size="small"
                                    {...input}
                                    value={input && input.value ? input.value : null}
                                    className={classes.textField}
                                    label="Birthdate"
                                    format="DD/MM/YYYY"
                                    placeholder="dd/mm/yyyy"
                                    animateYearScrolling={true}
                                    disabled={values.otherPartyNameGuid}
                                    // tslint:disable-next-line: max-line-length
                                    autoOk={true}
                                    allowKeyboardControl={false}
                                    variant={'inline'}
                                    error={meta.error && meta.touched}
                                    helperText={meta.error}
                                    inputProps={{ autoComplete: 'off' }}
                                    InputLabelProps={{ shrink: true }}
                                    maxDate={new Date()}
                                    onChange={(date: MaterialUiPickersDate | null, value?: string | null) => input.onChange(date)}
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={6} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyOrganisation"
                            // validate={required}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    multiline={true}
                                    label={translate('Organisation')}
                                    className={classes.textField}
                                    disabled={values.otherPartyNameGuid}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Field is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={12} className={clsx(classes.customFieldGrid, classes.fieldRowGap)}>
                        <Field
                            name="otherPartyNotes"
                            // validate={required}
                            subscription={{ touched: true, error: true, value: true }}
                        >
                            {({ input, meta }) => (
                                <TextField
                                    variant="outlined"
                                    size="small"
                                    {...input}
                                    multiline={true}
                                    label="Notes"
                                    className={classes.textField}
                                    error={meta.error && meta.touched}
                                    autoComplete="abcd"
                                    helperText={
                                        meta.error &&
                                            meta.touched
                                            ? 'Notes is required'
                                            : ''
                                    }
                                />
                            )}
                        </Field>
                    </Grid>
                    <Grid item={true} xs={12} md={12}>
                        <OtherPartyAddressPanel
                            form={form}
                            classes={classes}
                            defaultExpanded={true}
                            homeCountry={homeCountry}
                            getAddressLabelForPanel={getAddressLabelForPanel}
                            createAddressSelectionHandler={createAddressSelectionHandler}
                            createInputChangedHandler={createInputChangedHandler}
                        />
                    </Grid>
                    <Grid item={true} xs={12} md={12}>
                        <OtherPartyPostalAddressPanel
                            form={form}
                            classes={classes}
                            isExpanded={isOtherSidePostalAddressPanelExpanded}
                            homeCountry={homeCountry}
                            isOtherPartyPoBoxRequired={isOtherPartyPoBoxRequired}
                            requiredValidator={requiredValidator}
                            createAddressSelectionHandler={createAddressSelectionHandler}
                            createInputChangedHandler={createInputChangedHandler}
                            onExpandChange={onOtherSidePostalAddressPanelExpandChange}
                        />
                    </Grid>
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
}, arePropsEqualIgnoringForm);
OtherSidePanelBody.displayName = 'OtherSidePanelBody';

export const OtherSidePanel: React.FC<OtherSidePanelProps> = React.memo((panelProps) => (
    <FormSpy subscription={{ values: true }}>
        {({ values }) => (
            <OtherSidePanelBody
                {...panelProps}
                formValues={{
                    otherPartyTitle: values.otherPartyTitle,
                    otherPartyFirstName: values.otherPartyFirstName,
                    otherPartyLastName: values.otherPartyLastName,
                    otherPartyFloor: values.otherPartyFloor,
                    otherPartyNumber: values.otherPartyNumber,
                    otherPartyStreet: values.otherPartyStreet,
                    otherPartyStreet2: values.otherPartyStreet2,
                    otherPartySuburb: values.otherPartySuburb,
                    otherPartyState: values.otherPartyState,
                    otherPartyPostcode: values.otherPartyPostcode,
                    otherPartyCountry: values.otherPartyCountry,
                    otherPartyNameGuid: values.otherPartyNameGuid,
                    isOtherPartyNameSwitched: values.isOtherPartyNameSwitched,
                    otherPartyIndividualSelector: values.otherPartyIndividualSelector,
                }}
            />
        )}
    </FormSpy>
), arePropsEqualIgnoringForm);
OtherSidePanel.displayName = 'OtherSidePanel';
